import React, {
    Children,
    cloneElement,
    HTMLAttributes,
    isValidElement,
    ReactElement,
    useState,
} from "react";
import { createPortal } from "react-dom";
import mergeRefs from "merge-refs";
import { usePopper } from "react-popper";

import { useToggle } from "@formant/ui/utils/useToggle";

import { getPortalElement } from "../getPortalElement";
import { useOnClickOutside } from "./useOnClickOutside";

interface IPopupPortalProps<T extends HTMLElement> {
    isOpen: boolean;
    children: ReactElement<HTMLAttributes<T>>;
    context: ReturnType<typeof usePopper>;
}

export function PopupPortal<T extends HTMLElement>(
    props: IPopupPortalProps<T>,
) {
    const { isOpen, children, context } = props;
    return isOpen
        ? createPortal(
              Children.map(Children.only(children), el => {
                  if (!isValidElement(el)) {
                      return null;
                  }

                  return cloneElement<any>(el, {
                      ...context.attributes.popper,
                      style: {
                          ...context.styles.popper,
                          ...el.props.style,
                      },
                  });
              }),
              getPortalElement(),
          )
        : null;
}

export function usePopup<
    TRef extends HTMLElement = HTMLElement,
    TTarget extends HTMLElement = HTMLElement,
>(
    popperOptions: Parameters<typeof usePopper>[2],
): {
    isOpen: boolean;
    openDropdown: () => void;
    toggleDropdown: () => void;
    closeDropdown: () => void;
    setRefElement: React.Ref<TRef> | React.RefCallback<TRef>;
    setTargetElement: React.Ref<TTarget> | React.RefCallback<TTarget>;
    context: ReturnType<typeof usePopper>;
} {
    const [
        isOpen,
        { off: closeDropdown, on: openDropdown, toggle: toggleDropdown },
    ] = useToggle(false);

    const clickOutsideRef = useOnClickOutside(closeDropdown);

    const [refElement, setRefElement] = useState<TRef | null>(null);
    const [popElement, setPopElement] = useState<TTarget | null>(null);

    const context = usePopper(refElement, popElement, popperOptions);

    const setTargetElement = mergeRefs<TTarget>(setPopElement, clickOutsideRef);

    return {
        isOpen,
        openDropdown,
        toggleDropdown,
        closeDropdown,
        setRefElement,
        setTargetElement,
        context,
    };
}
